import { type MappingDomain, mappingDomains } from "@nexxt/contracts/reference";
import { externalSystems } from "@nexxt/contracts/til";
import { m } from "../../../paraglide/messages.js";

// The words of the codes of the other systems: a domain or a system the contract gains needs its text here.

export type ExternalSystem = (typeof externalSystems)[number];

export const systemLabel: Record<ExternalSystem, () => string> = {
  business_central: m.sync_target_business_central,
  til: m.mapping_system_til,
  joor: m.sync_target_joor,
  hubspot: m.mapping_system_hubspot,
  ims: m.mapping_system_ims,
  gxo: m.mapping_system_gxo,
  document_service: m.mapping_system_document_service,
  zendesk: m.mapping_system_zendesk,
  tlg_shipping: m.mapping_system_tlg_shipping,
  doc_extraction: m.mapping_system_doc_extraction,
  netsuite_legacy: m.mapping_system_netsuite_legacy,
  zedonk_legacy: m.mapping_system_zedonk_legacy,
};

export const domainLabel: Record<MappingDomain, () => string> = {
  brand: m.mapping_domain_brand,
  season: m.reference_dataset_season,
  gender: m.reference_dataset_gender,
  product_category: m.reference_dataset_product_category,
  product_subcategory: m.reference_dataset_product_subcategory,
  size_run_category: m.reference_dataset_size_run_category,
  size_run: m.reference_dataset_size_run,
  size: m.reference_dataset_size,
  colour: m.reference_dataset_colour,
  customs_classification: m.reference_dataset_customs_classification,
  price_list: m.reference_dataset_price_list,
  weight_unit: m.reference_dataset_weight_unit,
  supplier: m.mapping_domain_supplier,
  location: m.reference_dataset_location,
  payment_terms: m.reference_dataset_payment_terms,
  delivery_term: m.reference_dataset_delivery_term,
};

/** The systems whose missing code a refusal names in its field (`joor.size_run`). */
const mappingSystems: readonly ExternalSystem[] = ["business_central", "joor", "gxo", "ims"];
const camel = (name: string) => name.replace(/_(\w)/g, (_, letter: string) => letter.toUpperCase());

/**
 * The field of a mapping issue names the reference data and the system that lacks its code (`joor.size_run`,
 * `business_central.location`, `gxo.location`): in words, with the name `fieldLabel` gives the data. Undefined for any
 * other field.
 */
export function mappingLabel(field: string, fieldLabel: (field: string) => string): string | undefined {
  const [system = "", domain] = field.split(".");
  const known = mappingSystems.find((s) => s === system);
  if (domain === undefined || known === undefined) return undefined;
  return m.issue_mapping_field({ domain: fieldLabel(camel(domain)), system: systemLabel[known]() });
}

/**
 * The system and the reference data of a missing or ambiguous mapping (`business_central.location`, rule
 * `missing_mapping`), whose codes the user can fill in; undefined for another issue.
 */
export function mappingOf({ field, rule }: { field: string; rule: string }) {
  const [system, domain] = field.split(".");
  const knownSystem = externalSystems.find((s) => s === system);
  const knownDomain = mappingDomains.find((d): d is MappingDomain => d === domain);
  return knownSystem && knownDomain && rule.endsWith("_mapping")
    ? { system: knownSystem, domain: knownDomain }
    : undefined;
}
